import { type Node, type NodeProps, Position } from '@xyflow/react';
import React from 'react';

import type { LegalVarianceDeterminationBlockConfig } from '@mai/types';

import { createHandleId } from '../../utils/handle-ids';
import { BaseNode, type NodeData } from './BaseNode';
import { NodeHandle } from './NodeHandle';

export type LegalVarianceDeterminationNodeType = Node<
  NodeData,
  'custom_legal_variance_determination'
>;

export const LegalVarianceDeterminationNode: React.FC<
  NodeProps<LegalVarianceDeterminationNodeType>
> = ({ id, data, selected }) => {
  const config = data.config as LegalVarianceDeterminationBlockConfig;
  const isConfigured = config.general_instructions;

  const subtitle = isConfigured
    ? `General Instructions: ${config.general_instructions}`
    : 'Not configured';

  // Extract connection state from node data for visual indicators
  const activeConnection = data.activeConnection;
  const isValidTargetHandle = data.isValidTargetHandle as
    | ((targetHandle: string) => boolean)
    | undefined;
  const isActiveConnection = !!activeConnection;

  return (
    <BaseNode
      nodeId={id}
      data={data}
      category="processing"
      subtitle={subtitle}
      selected={selected}
    >
      {/* Input handles - 4 ports */}
      <NodeHandle
        type="target"
        position={Position.Left}
        id="extracted_clauses"
        blockId={id}
        style={{ top: '15%' }}
        isActiveConnection={isActiveConnection}
        isValidTarget={
          isValidTargetHandle
            ? isValidTargetHandle(createHandleId(id, 'extracted_clauses'))
            : undefined
        }
      />
      <NodeHandle
        type="target"
        position={Position.Left}
        id="clause_definition_dataset_id"
        blockId={id}
        style={{ top: '38%' }}
        isActiveConnection={isActiveConnection}
        isValidTarget={
          isValidTargetHandle
            ? isValidTargetHandle(
                createHandleId(id, 'clause_definition_dataset_id'),
              )
            : undefined
        }
      />
      <NodeHandle
        type="target"
        position={Position.Left}
        id="benchmark_dataset_ids"
        blockId={id}
        style={{ top: '62%' }}
        isActiveConnection={isActiveConnection}
        isValidTarget={
          isValidTargetHandle
            ? isValidTargetHandle(createHandleId(id, 'benchmark_dataset_ids'))
            : undefined
        }
      />
      <NodeHandle
        type="target"
        position={Position.Left}
        id="research_dataset_ids"
        blockId={id}
        style={{ top: '85%' }}
        isActiveConnection={isActiveConnection}
        isValidTarget={
          isValidTargetHandle
            ? isValidTargetHandle(createHandleId(id, 'research_dataset_ids'))
            : undefined
        }
      />

      {/* Output handle - single results port */}
      <NodeHandle
        type="source"
        position={Position.Right}
        id="results"
        blockId={id}
        style={{ top: '50%' }}
        isActiveConnection={isActiveConnection}
      />
    </BaseNode>
  );
};
